import React, { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSignedUrl, assetQueryKey } from "../services/useSignedUrl";

type SignedUrlImageProps = Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> & {
  /** S3 object key (filename or path segment). */
  s3Key: string;
  /** Unused; kept for call-site compatibility. */
  bucket?: string;
  /** Path prefix; default "app/assets/images". */
  path?: string;
};

export function SignedUrlImage({
  s3Key,
  bucket,
  path = "app/assets/images",
  onError: callerOnError,
  ...imgProps
}: SignedUrlImageProps) {
  const queryClient = useQueryClient();
  const url = useSignedUrl(s3Key, bucket, path);
  const queryKey = assetQueryKey(s3Key, path);
  const [hasRetried, setHasRetried] = useState(false);

  // Reset retry budget whenever the target asset changes (key or path).
  useEffect(() => { setHasRetried(false); }, [queryKey[1]]);

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    if (!hasRetried) {
      setHasRetried(true);
      // After 1.5s, fetch a fresh signed URL from the backend. The new URL
      // causes a re-render so the browser retries with no cached failure.
      // Do NOT modify the signed URL — S3 Signature V4 covers every query
      // parameter; any addition causes SignatureDoesNotMatch (403).
      // One retry per asset to avoid an infinite loop on missing objects.
      setTimeout(() => queryClient.invalidateQueries({ queryKey }), 1500);
    }
    callerOnError?.(e);
  };

  if (!url) return null;

  return <img src={url} onError={handleError} alt="" {...imgProps} />;
}
